import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getModuleLearnerV2, GetModuleLearnerV2OutputType } from 'zite-endpoints-sdk';
import { useDebouncedCallback } from 'use-debounce';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, WifiOff, Save, ShieldAlert, CloudOff, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { enqueue, drainQueue, getQueue, getQueueStatus } from '@/lib/mutationQueue';
import { applyMutation } from 'zite-endpoints-sdk';
import { toast } from 'sonner';

import StepIndicator from '@/components/interactive-learner/StepIndicator';
import WelcomeStep from '@/components/interactive-learner/WelcomeStep';
import WatchStep from '@/components/interactive-learner/WatchStep';
import LearnStep from '@/components/interactive-learner/LearnStep';
import PractiseStep from '@/components/interactive-learner/PractiseStep';
import CheckStep from '@/components/interactive-learner/CheckStep';
import ReadinessStep from '@/components/interactive-learner/ReadinessStep';
import SummaryStep from '@/components/interactive-learner/SummaryStep';

type LearnerData = GetModuleLearnerV2OutputType;
const DRAFT_KEY = (enrollmentId: string, moduleId: string) => `rb-learner-v2-${enrollmentId}-${moduleId}`;

function SaveIndicator({ state, queueCount }: { state: string; queueCount: number }) {
  if (state === 'offline') return <span className="text-xs text-destructive flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline{queueCount > 0 ? ` (${queueCount} queued)` : ''}</span>;
  if (state === 'syncing') return <span className="text-xs text-primary flex items-center gap-1 animate-pulse"><RefreshCw className="w-3 h-3 animate-spin" /> Syncing…</span>;
  if (state === 'error') return <span className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Needs attention</span>;
  if (state === 'saving') return <span className="text-xs text-primary flex items-center gap-1 animate-pulse"><Save className="w-3 h-3" /> Saving…</span>;
  if (state === 'queued') return <span className="text-xs text-amber-500 flex items-center gap-1"><CloudOff className="w-3 h-3" /> Queued ({queueCount})</span>;
  if (state === 'unsaved') return <span className="text-xs text-muted-foreground flex items-center gap-1"><Save className="w-3 h-3" /> Unsaved</span>;
  return <span className="text-xs text-primary flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>;
}

export default function InteractiveLearner() {
  const { enrollmentId, moduleId } = useParams<{ enrollmentId: string; moduleId: string }>();
  const { user } = useAuth();
  const userId = user?.id || '';
  const [data, setData] = useState<LearnerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [videoPercent, setVideoPercent] = useState(0);
  const [saveState, setSaveState] = useState<string>('saved');
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);

  // Stable readiness UUID per module — persists across retries
  const readinessUuidRef = useRef<string>('');
  const getReadinessUuid = useCallback(() => {
    if (!readinessUuidRef.current) {
      const key = `rb-readiness-uuid-${enrollmentId}-${moduleId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        readinessUuidRef.current = stored;
      } else {
        readinessUuidRef.current = crypto.randomUUID();
        localStorage.setItem(key, readinessUuidRef.current);
      }
    }
    return readinessUuidRef.current;
  }, [enrollmentId, moduleId]);

  const applyFn = useCallback(async (m: { id: string; mutationType: string; payload: Record<string, unknown> }) => {
    try {
      await applyMutation({ clientMutationId: m.id, mutationType: m.mutationType as any, payload: m.payload });
      return true;
    } catch { return false; }
  }, []);

  const doDrain = useCallback(async () => {
    if (!userId) return;
    setSaveState('syncing');
    await drainQueue(userId, applyFn);
    setQueueCount(getQueue(userId).length);
    setSaveState(getQueue(userId).length > 0 ? 'queued' : 'saved');
  }, [userId, applyFn]);

  useEffect(() => {
    const on = () => {
      setOnline(true);
      setSaveState(p => p === 'offline' ? 'unsaved' : p);
      doDrain();
    };
    const off = () => { setOnline(false); setSaveState('offline'); };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, [doDrain]);

  // Drain queue on initial mount if online
  useEffect(() => {
    if (userId && navigator.onLine) {
      doDrain();
    }
  }, [userId, doDrain]);

  useEffect(() => {
    if (!user || !enrollmentId || !moduleId) return;
    getModuleLearnerV2({ enrollmentId, moduleId })
      .then(d => {
        setData(d);
        setVideoPercent(d.progress.videoPercent);
        const done = new Set<string>();
        if (d.progress.welcomeComplete) done.add('welcome');
        if (d.progress.watchComplete) done.add('watch');
        if (d.progress.learnComplete) done.add('learn');
        if (d.progress.practiseComplete) done.add('practise');
        if (d.progress.checkComplete) done.add('check');
        if (d.progress.readinessComplete) done.add('readiness');
        setCompletedSteps(done);
        const steps = parseSteps(d.module.stepsEnabled);
        const firstIncomplete = steps.findIndex(s => !done.has(s));
        if (firstIncomplete >= 0) setCurrentStep(firstIncomplete);
        else setCurrentStep(steps.length - 1);
      })
      .catch(e => setError(e?.message || 'Could not load module'))
      .finally(() => setLoading(false));
  }, [user, enrollmentId, moduleId]);

  const debouncedSave = useDebouncedCallback(async (updates: Record<string, unknown>) => {
    if (!data || !userId) return;
    const mutationId = crypto.randomUUID();
    if (!online) {
      enqueue(userId, { id: mutationId, mutationType: 'saveLearnerProgress', payload: { progressId: data.progress.id, ...updates } });
      setQueueCount(getQueue(userId).length);
      setSaveState('queued');
      return;
    }
    setSaveState('saving');
    try {
      await applyMutation({ clientMutationId: mutationId, mutationType: 'saveLearnerProgress', payload: { progressId: data.progress.id, ...updates } });
      setSaveState('saved');
    } catch {
      enqueue(userId, { id: mutationId, mutationType: 'saveLearnerProgress', payload: { progressId: data.progress.id, ...updates } });
      setQueueCount(getQueue(userId).length);
      setSaveState('queued');
    }
  }, 1500);

  const markStepComplete = useCallback((step: string, updates?: Record<string, unknown>) => {
    setCompletedSteps(prev => { const next = new Set(prev); next.add(step); return next; });
    const saveUpdates: Record<string, unknown> = { ...updates };
    if (step === 'welcome') saveUpdates.welcomeComplete = true;
    if (step === 'watch') saveUpdates.watchComplete = true;
    if (step === 'learn') saveUpdates.learnComplete = true;
    if (step === 'practise') saveUpdates.practiseComplete = true;
    debouncedSave(saveUpdates);
    if (enrollmentId && moduleId) {
      try {
        const key = DRAFT_KEY(enrollmentId, moduleId);
        const draft = JSON.parse(localStorage.getItem(key) || '{}');
        draft[step] = true;
        if (updates) Object.assign(draft, updates);
        localStorage.setItem(key, JSON.stringify(draft));
      } catch {}
    }
  }, [debouncedSave, enrollmentId, moduleId]);

  const handleVideoProgress = useCallback((pct: number) => {
    setVideoPercent(prev => {
      const next = Math.max(prev, pct);
      if (next > prev) debouncedSave({ videoPercent: next });
      return next;
    });
  }, [debouncedSave]);

  const goToNextStep = useCallback(() => {
    if (!data) return;
    const steps = parseSteps(data.module.stepsEnabled);
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data]);

  /** Queued readiness submission — uses stable UUID, enqueues, drains if online */
  const handleSubmitReadiness = useCallback(async (notes: string): Promise<boolean> => {
    if (!data || !userId) return false;
    const uuid = getReadinessUuid();
    const payload = { progressId: data.progress.id, readinessNotes: notes };

    if (!online) {
      enqueue(userId, { id: uuid, mutationType: 'submitReadiness', payload });
      setQueueCount(getQueue(userId).length);
      setSaveState('queued');
      toast.info('Readiness queued — will sync when back online');
      return true;
    }

    setSaveState('syncing');
    try {
      await applyMutation({ clientMutationId: uuid, mutationType: 'submitReadiness', payload });
      setSaveState('saved');
      toast.success('Readiness confirmed');
      return true;
    } catch {
      enqueue(userId, { id: uuid, mutationType: 'submitReadiness', payload });
      setQueueCount(getQueue(userId).length);
      setSaveState('queued');
      toast.info('Readiness queued — will retry');
      return true;
    }
  }, [data, userId, online, getReadinessUuid]);

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-48" /><Skeleton className="h-12 w-full" /><Skeleton className="h-40" /><Skeleton className="h-40" />
    </div>
  );

  if (error || !data) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <ShieldAlert className="w-12 h-12 text-destructive mx-auto mb-3" />
      <h2 className="font-semibold text-foreground mb-1">Cannot Access Module</h2>
      <p className="text-sm text-muted-foreground mb-4">{error || 'This module is not available.'}</p>
      <Link to={`/train/${enrollmentId}`} className="text-sm text-primary hover:underline">← Back to Course</Link>
    </div>
  );

  const steps = parseSteps(data.module.stepsEnabled);
  const stepName = steps[currentStep] || 'welcome';

  return (
    <div className="max-w-lg mx-auto pb-6">
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-40">
        <div className="flex items-center justify-between mb-2">
          <Link to={`/train/${enrollmentId}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            <span className="truncate max-w-[160px]">{data.module.displayLabel}</span>
          </Link>
          <SaveIndicator state={saveState} queueCount={queueCount} />
        </div>
        <StepIndicator steps={steps} current={currentStep} completedSteps={completedSteps}
          onStepClick={(i) => { if (i <= currentStep || completedSteps.has(steps[i])) setCurrentStep(i); }} />
      </div>

      <div className="px-4 py-4">
        {stepName === 'welcome' && (
          <WelcomeStep module={data.module} onContinue={() => { markStepComplete('welcome'); goToNextStep(); }} />
        )}
        {stepName === 'watch' && (
          <WatchStep videoUrl={data.module.videoUrl} requiredPercent={data.module.videoRequiredPercent}
            initialPercent={videoPercent} onProgress={handleVideoProgress}
            onContinue={() => { markStepComplete('watch', { videoPercent }); goToNextStep(); }} />
        )}
        {stepName === 'learn' && (
          <LearnStep keyPoints={data.keyPoints} commonMistakes={data.module.commonMistakes}
            referenceMaterials={data.module.referenceMaterials}
            onContinue={() => { markStepComplete('learn'); goToNextStep(); }} />
        )}
        {stepName === 'practise' && (
          <PractiseStep scenarios={data.scenarios}
            onContinue={() => { markStepComplete('practise'); goToNextStep(); }} />
        )}
        {stepName === 'check' && (
          <CheckStep questions={data.questions} progressId={data.progress.id}
            moduleId={data.module.id} enrollmentId={enrollmentId || ''}
            alreadyPassed={data.progress.checkPassed} previousScore={data.progress.checkScore}
            attempts={data.checkAttempts}
            onComplete={(passed) => { if (passed) { setCompletedSteps(prev => { const n = new Set(prev); n.add('check'); return n; }); goToNextStep(); } }} />
        )}
        {stepName === 'readiness' && (
          <ReadinessStep progressId={data.progress.id}
            alreadyComplete={data.progress.readinessComplete}
            existingNotes={data.progress.readinessNotes}
            onSubmitReadiness={handleSubmitReadiness}
            saveState={saveState}
            onComplete={() => { setCompletedSteps(prev => { const n = new Set(prev); n.add('readiness'); return n; }); goToNextStep(); }} />
        )}
        {stepName === 'summary' && (
          <SummaryStep module={data.module} progress={data.progress}
            enrollmentId={enrollmentId || ''} checkAttempts={data.checkAttempts} />
        )}
      </div>
    </div>
  );
}

function parseSteps(stepsEnabled: string): string[] {
  if (!stepsEnabled) return ['welcome', 'watch', 'learn', 'practise', 'check', 'readiness', 'summary'];
  return stepsEnabled.split(',').map(s => s.trim()).filter(Boolean);
}
